// @flow
import {path, pathOr} from 'ramda';

import type {CategoryProductItemType} from 'constants/flow/flowTypesCategory';
import {LENS_TYPES} from 'constants/analytics/globalDataLayer';
import {PRODUCT_TYPES} from 'constants/product';
import type {OrderReviewItemType} from 'constants/flow/flowTypesCheckout';
import {getMsMediaSourceUrl} from 'libs/utils/utils.js';
import {type QuoteItemType} from 'constants/flow/flowTypesCart';

type PossibleValuesType = string | null | typeof undefined; /* eslint-disable-line no-undefined */

const badges = [
    {id: 71, label: 'New'},
    {id: 127, label: 'Premium Frames'},
    {id: 131, label: 'Is Sale'},
    {id: 231, label: 'Top Rated'},
];

/**
 * Returns lenses type by received ID.
 *
 * @param {number} id - Received lenses type id.
 * @returns {string} - Returned lenses type.
 */
export const getLensType = (id: number): string => LENS_TYPES[id];

/**
 * Get second image value.
 *
 * @param {SimpleProductType} children - Received children.
 * @returns {string} - Returned second image value.
 */
export const getSecondImageValue = (children: CategoryProductItemType): string => {
    const src = path(['media', 'front', 'src'], children);
    return src ? getMsMediaSourceUrl(src) : '-';
};

/**
 * Check than incoming value is not equal to null, zero, empty string and undefined and return correct value
 *
 * @param {PossibleValuesType} value - Received value.
 * @returns {string} - Returned value.
 */
export const emptyStringIsFalsy = (value: PossibleValuesType): string => value || '';

/**
 * Get color safe value of children product.
 *
 * @param {SimpleProductType} children - Received children.
 * @returns {string} - Returned color value.
 */
export const getColorValue = (children: any): string => children?.color?.label || '';

/**
 * Get frame size value.
 *
 * @param {SimpleProductType} children - Received children.
 * @returns {string} - Frame size value.
 */
export const getFrameSizeValue = (children: any): string => children?.frame?.size?.shortLabel || '';

/**
 * Get lenses type value.
 *
 * @param {SimpleProductType} children - Received children.
 * @returns {string} - Lenses type value.
 */
export const getLensTypeValue = (children: any): string =>
    children.lensTypeIds ? children.lensTypeIds.map(getLensType).join(' / ') : '';

/**
 * Get glasses type.
 *
 * @param {SimpleProductType} children - Received children.
 * @returns {string} - glasses type value.
 */
export const getGlassesType = (children: any): string =>
    Array.isArray(children.glassesTypeIds)
        ? children.glassesTypeIds.join(' / ')
        : children.glassesTypeIds || '';

/**
 * Get attribute.
 *
 * @param {string} type - Product type.
 * @returns {string} - Attribute.
 */
export const getAttribute = (type: string): string =>
    type === PRODUCT_TYPES.CONTACT_LENSES ? 'lenses' : 'frames';

/**
 * Get the badge for the product.
 *
 * @param {Array<number> | string} badgesList - Badges list.
 * @returns {string} Badge value.
 */
export const getProductBadge = (badgesList: any): string =>
    emptyStringIsFalsy(
        Array.isArray(badgesList)
            ? badgesList
                  .reduce((accumulator: Array<string>, {id}: {id: number}): Array<string> => {
                      const badge = badges.find(item => item.id === id);
                      return badge ? [...accumulator, badge.label] : accumulator;
                  }, [])
                  .join(' / ')
            : badgesList,
    );

/**
 * Get gender.
 *
 * @param {Array<number |string>} gender - Gender.
 * @returns {string} - String with gender.
 */
export const getGender = (gender: $ReadOnlyArray<number | string>): string =>
    Array.isArray(gender) ? gender.join(' / ') : gender;

/**
 * Get string of array values.
 *
 * @param {Array<number | string>} value - array.
 * @returns {string} - string of values.
 */
export const arrayToString = (value: $ReadOnlyArray<number | string>): string => {
    return Array.isArray(value) ? value.join(' / ') : '';
};

/**
 * Get attribute by boolean param.
 *
 * @param {boolean} value - is contact lens
 * @returns {string} - type product
 */
export const getAttributeBoolean = (value: boolean): string => {
    return value ? 'lenses' : 'frames';
};

/**
 * Get frame size
 *
 * @param {SimpleProductType} children - Received children
 * @returns {string} - frame size
 */
export const getFrameSize = (children: OrderReviewItemType): string => {
    return children.frame && children.frame.frameSize
        ? emptyStringIsFalsy(children.frame.frameSize[0])
        : '';
};

/**
 * Get price of simple product
 * @param {QuoteItemType} children - Received children
 * @returns {string} - price
 */
export const getPrice = (children: QuoteItemType): string => {
    return children.frame ? children.frame.price : children.price;
};

/**
 * Get color of simple product
 * @param {OrderReviewItemType} children - Received children product
 * @returns {string} - color
 */
export const getColor = (children: OrderReviewItemType): string => {
    return children.frame ? children.frame.color : '';
};

/**
 * Get formatted string of color ids
 * @param {any} children - Received children product
 * @returns {string} - adapted string of color ids
 */
export const getColorIds = (children: any): string =>
    pathOr('', ['color', 'id'], children).replace(/_/g, ' / ');

/**
 * Get an array with frame size id
 * @param {any} children - Received children product
 * @returns {null | number[]} - array with frame size id or empty string
 */
export const getFrameSizeAsArrayOrNull = (children: any): null | number[] => {
    const sizeId = children?.frame?.size?.id;
    return sizeId ? [sizeId] : null;
};
